import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Form, Button, message } from 'antd';
import { useTranslation } from 'react-i18next';
import FileUploader from 'components/features/file-uploader';
import {
  ContractResponseDto,
  ContractResponseDtoContractStatusEnum,
  ContractResponseDtoTransactionTypeEnum,
  CreateUploadedFilePayloadDtoEntityTypeEnum,
  SubmissionResponseDto,
  SyncDeclarationDetailPayloadDto,
  SyncInstallmentDetailPayloadDto,
  UpdateContractPayloadDto,
  UpdateContractPayloadDtoTransactionTypeEnum,
} from 'libs/pinnacle-sdk';
import TransactionDetailsSection from './transaction-details';
import BinderPlacementSection from './binder-placement';
import InstallmentDetailsSection, {
  InstallmentData,
} from './installment-details';
import DeclarationDetailsSection, {
  DeclarationData,
} from './declaration-detail';
import IncidentalSection from './incidental';
import BinderScheduleSection from './binder-schedule';
import ClosingReasonSection from './closing-reason';
import NotesAndEndorsementsSection from './notes-and-endorsements';
import {
  ContractDetailFormValues,
  getInitialValues,
  getValidationSchema,
  shouldShowClosingReason,
  shouldShowDetailContent,
} from './form';
import { useWatch } from 'antd/es/form/Form';
import { handleError, validateForm } from 'utils/form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { EQueryKeys } from 'constants/query-keys';
import {
  contractApi,
  layerResultApi,
  premiumScheduleApi,
  referenceDataApi,
} from 'services/apis';
import {
  BinderScheduleItem,
  calculateBinderSchedule,
  EBinderScheduleKey,
} from 'components/features/binding/contract-detail/binder-schedule/form';
import dayjs from 'dayjs';
import { useFileSession } from 'hooks/useFileSession';
import FormActionButtons from 'components/ui/form-action-buttons';
import FormSection from 'components/ui/form-section';
import SubmissionCurrencyTab from 'components/features/submission-currency-tabs';
import { DEFAULT_CONVERSION_CURRENCY } from 'components/features/submission-details/types';
import { EFileStatus } from 'components/features/file-uploader/enum';
import { isEqual } from 'lodash';
import { cn } from 'utils';

interface ContractDetailProps {
  submission: SubmissionResponseDto;
  handleBackToBindingDashboard: () => void;
  contractId: string | null;
  isSlipUpload?: boolean;
  disabled?: boolean;
  hiddenReferenceNumber?: boolean;
  hideAllActions?: boolean;
  isCloseContractDetail?: boolean;
  enableFileUpload?: boolean;
  isCancelledOrNotTakenUpContract?: boolean;
}

const ContractDetail: React.FC<ContractDetailProps> = ({
  submission,
  handleBackToBindingDashboard,
  contractId,
  isSlipUpload,
  disabled = false,
  hiddenReferenceNumber = false,
  hideAllActions = false,
  isCloseContractDetail = false,
  enableFileUpload = false,
  isCancelledOrNotTakenUpContract = false,
}) => {
  const [form] = Form.useForm<ContractDetailFormValues>();
  const { t } = useTranslation();
  const [isFormValid, setIsFormValid] = useState(false);
  const [isInstallmentsValid, setIsInstallmentsValid] = useState(true);
  const [isBinderScheduleValid, setIsBinderScheduleValid] = useState(true);
  const [isDeclarationValid, setIsDeclarationValid] = useState(true);
  const installments = useWatch<boolean>('installments', form);
  const isDeclaration = useWatch<boolean | null>('isDeclaration', form);
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<string>(
    submission.originalCurrency || DEFAULT_CONVERSION_CURRENCY
  );

  const contractStatus = useWatch('contractStatus', form);

  const isFormDisabled = useMemo(() => {
    return (
      disabled ||
      isSlipUpload ||
      isCloseContractDetail ||
      isCancelledOrNotTakenUpContract
    );
  }, [disabled, isSlipUpload, isCloseContractDetail]);

  const { data: contract } = useQuery(
    [EQueryKeys.CONTRACT_BY_ID, contractId],
    () => {
      if (!contractId) {
        return undefined;
      }
      return contractApi.getContractById({
        contractId,
        applyAdjustments: true,
      });
    },
    {
      enabled: !!contractId,
      refetchOnWindowFocus: false,
    }
  );

  const { data: tbaConfig } = useQuery(
    [EQueryKeys.TBA_CONFIG],
    () => referenceDataApi.getTbaEnv(),
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  const tbaReinsuredIds = tbaConfig?.tbaReinsuredIDs || [];
  const tbaBrokerIds = tbaConfig?.tbaBrokerIDs || [];

  const originalContractStatus = contract?.contractStatus;

  const { fileSessionId, fileList, setFileList } = useFileSession(
    contract?.files
  );

  const layerPremium = Number(useWatch('layerPremium', form));

  const [binderScheduleData, setBinderScheduleData] = useState<
    BinderScheduleItem[]
  >([]);

  const [installmentData, setInstallmentData] = useState<InstallmentData[]>([
    {
      id: 1,
      installmentPercent: 50,
      installmentDueDate: dayjs().startOf('day'),
    },
    {
      id: 2,
      installmentPercent: 50,
      installmentDueDate: dayjs().startOf('day'),
    },
  ]);

  const [declarationData, setDeclarationData] = useState<DeclarationData[]>([]);

  const isDeclarationDataValid = useMemo(() => {
    return isDeclaration === true ? declarationData.length > 0 : true;
  }, [isDeclaration, declarationData.length]);

  const isDataValid = useMemo(
    () =>
      isFormValid &&
      isInstallmentsValid &&
      isBinderScheduleValid &&
      isDeclarationValid &&
      isDeclarationDataValid,
    [
      isFormValid,
      isInstallmentsValid,
      isBinderScheduleValid,
      isDeclarationValid,
      isDeclarationDataValid,
    ]
  );

  useEffect(() => {
    if (isDeclaration === false) {
      setDeclarationData([]);
      setIsDeclarationValid(true);
    } else if (isDeclaration === true) {
      if (
        contract?.declarationSchedules &&
        contract?.declarationSchedules.length > 0
      ) {
        if (declarationData.length === 0) {
          const apiDeclarationData = contract?.declarationSchedules.map(
            (item, index) => ({
              id: index + 1,
              declarationName: item.declarationName,
              entityName: item.entityName,
              country: item.country,
              programOrder: Number(item.programOrder),
              programOrderTotalPremium: Number(item.programOrderTotalPremium),
              shareOfPremiumSigned: 0,
              declarationId: item.declarationId,
              dueDate: item.dueDate ? dayjs(item.dueDate) : undefined,
            })
          );
          setDeclarationData(apiDeclarationData);
        }
      } else {
        if (declarationData.length === 0) {
          const defaultDeclarationData = [
            {
              id: 1,
              declarationName: '',
              entityName: '',
              country: '',
              programOrder: 0,
              programOrderTotalPremium: 0,
              shareOfPremiumSigned: 0,
              declarationId: undefined,
              dueDate: dayjs(),
            },
          ];
          setDeclarationData(defaultDeclarationData);
        }
      }
    }
  }, [contract?.declarationSchedules, isDeclaration]);

  useEffect(() => {
    if (isDeclaration === null || isDeclaration === undefined) {
      form.setFieldValue('isDeclaration', false);
      return;
    }

    if (isDeclaration === true) {
      form.setFieldValue('installments', false);
      setInstallmentData([]);
    }
  }, [isDeclaration, form]);

  useEffect(() => {
    if (installments === false) {
      setInstallmentData([]);
    } else if (installments === true) {
      setInstallmentData([
        {
          id: 1,
          installmentPercent: 50,
          installmentDueDate: dayjs().startOf('day'),
        },
        {
          id: 2,
          installmentPercent: 50,
          installmentDueDate: dayjs().startOf('day'),
        },
      ]);
    }
  }, [installments]);

  const showDetailContent = useMemo(() => {
    return shouldShowDetailContent(contractStatus);
  }, [contractStatus]);

  const showClosingReason = useMemo(() => {
    return shouldShowClosingReason(contractStatus);
  }, [contractStatus]);

  const { data: layerResults } = useQuery(
    [EQueryKeys.GET_LAYER_RESULTS, submission.submissionId],
    () => {
      return layerResultApi.getLayerResults({
        submissionId: submission.submissionId,
        layerResultsFilter: {
          includeAll: true,
          currency: submission.originalCurrency,
          types: [],
        },
      });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  const layerResult = useMemo(() => {
    return layerResults?.layers.find(
      (layer) => layer.layerNumber === contract?.layer?.layerNumber
    );
  }, [layerResults, contract]);

  useEffect(() => {
    async function checkInitialValues() {
      const initialValues = getInitialValues(
        contract?.submission || submission,
        contract,
        layerResult
      );
      form.setFieldsValue(initialValues);
      if (isFormDisabled) return;

      const isValid = await validateForm(
        initialValues,
        form,
        Object.keys(initialValues) as (keyof ContractDetailFormValues)[],
        getValidationSchema(t, { tbaReinsuredIds, tbaBrokerIds })
      );
      setIsFormValid(isValid);
    }
    checkInitialValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contract, layerResult, submission, tbaReinsuredIds, tbaBrokerIds]);

  const binderShareSigned = useWatch('binderShareSigned', form);

  const boundOrCloseContractMutation = useMutation(
    (payload: {
      contractId: string;
      updateContractPayloadDto: UpdateContractPayloadDto;
      installmentDetails?: SyncInstallmentDetailPayloadDto[];
      declarationDetails?: SyncDeclarationDetailPayloadDto[];
    }) => {
      return contractApi.bindOrCloseContract({
        contractId: payload.contractId,
        bindOrCloseContractPayloadDto: {
          contractPayload: payload.updateContractPayloadDto,
          installmentDetails: payload.installmentDetails,
          declarationDetails: payload.declarationDetails,
        },
      });
    },
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('contractDetail.updateContractFailed'),
        });
      },
      onSuccess: (response: ContractResponseDto) => {
        if (
          response.contractStatus !==
          ContractResponseDtoContractStatusEnum.Quoted
        ) {
          queryClient.invalidateQueries([
            EQueryKeys.SUBMISSION_BY_ID,
            submission.submissionId,
          ]);
        }
      },
    }
  );
  useEffect(() => {
    if (contract) {
      setBinderScheduleData(
        calculateBinderSchedule(
          [
            {
              key: EBinderScheduleKey.CEDING,
              label: t('contractDetail.cedingCommission'),
              rate: contract?.cedingCommissionRate
                ? Number(contract?.cedingCommissionRate) * 100
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.BROKERAGE,
              label: t('contractDetail.sourceBrokerage'),
              rate: contract?.sourceBrokerageRate
                ? Number(contract?.sourceBrokerageRate) * 100
                : undefined,
              isNegative: true,
            },
            {
              key: EBinderScheduleKey.ERE,
              label: t('contractDetail.ere'),
              rate: contract?.ereRate
                ? Number(contract?.ereRate) * 100
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.GST_ON_ERE,
              label: t('contractDetail.gstOnEre'),
              rate: contract?.gstOnEre
                ? Number(contract?.gstOnEre) * 100
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.OTHER_DEDUCTIONS,
              label: t('contractDetail.otherDeductions'),
              amount: contract?.otherDeductions
                ? Number(contract?.otherDeductions)
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.WITHHOLDING_TAX,
              label: t('contractDetail.withholdingTax'),
              rate: contract?.withholdingTaxRate
                ? Number(contract?.withholdingTaxRate) * 100
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.FEES,
              label: t('contractDetail.taxesAndFees'),
              amount: contract?.taxAndFeeAmount
                ? Number(contract?.taxAndFeeAmount)
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.AIT,
              label: t('contractDetail.ait'),
              rate: contract?.aitRate
                ? Number(contract?.aitRate) * 100
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.GST_CEDING,
              label: t('contractDetail.gstCeding'),
              rate: contract?.gstRateOnCedingCommission
                ? Number(contract?.gstRateOnCedingCommission) * 100
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.GST_BROKERAGE,
              label: t('contractDetail.gstSourceBrokerage'),
              rate: contract?.gstRateOnSourceBrokerage
                ? Number(contract?.gstRateOnSourceBrokerage) * 100
                : undefined,
              isNegative: true,
              isEditable: true,
            },
            {
              key: EBinderScheduleKey.GST_PREMIUM,
              label: t('contractDetail.gstPremium'),
              rate: contract?.gstRateOnPremium
                ? Number(contract?.gstRateOnPremium) * 100
                : undefined,
              isNegative: true,
            },
          ],
          {
            premium: layerPremium,
            binderShareSigned,
            brokerCountry: contract?.broker?.country,
            layerTotalDeductions: contract?.layer?.totalDeductions,
            binderPinCommission: contract?.binder?.pinCommission,
          },
          t,
          false
        )
      );
    }
  }, [contract, t, layerPremium, binderShareSigned]);

  const { data: installmentDetails } = useQuery(
    [EQueryKeys.GET_INSTALLMENT_DETAILS, contractId],
    () => {
      if (!contractId) {
        return undefined;
      }
      return premiumScheduleApi.getInstallmentDetailForContract({
        contractId,
      });
    },
    {
      enabled: !!contractId,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (
      installmentDetails?.premiumSchedules &&
      installmentDetails.premiumSchedules.length > 0
    ) {
      setInstallmentData(
        (installmentDetails?.premiumSchedules || []).map((item) => ({
          id: Number(item.installmentNumber),
          installmentPercent: Number(item.installmentPercent),
          installmentDueDate: dayjs(item.dueDate),
        }))
      );
    }
  }, [installmentDetails]);

  const haveFilesUploaded = useMemo(() => {
    return (
      fileList.length > 0 &&
      fileList.every((file) => file.status === EFileStatus.DONE)
    );
  }, [fileList]);

  const uploadSlipContractMutation = useMutation(
    (payload: {
      contractId: string;
      updateContractPayloadDto: UpdateContractPayloadDto;
    }) =>
      contractApi.uploadContractSlip({
        contractId: payload.contractId,
        uploadContractSlipPayloadDto: payload.updateContractPayloadDto,
      }),
    {
      onError: async (error: { response: Response }) => {
        handleError({
          error,
          defaultMessage: t('binding.updateContractError'),
        });
      },
    }
  );

  const handleCloseContract = useCallback(() => {
    if (!contractId) {
      return;
    }
    const values = form.getFieldsValue();
    return boundOrCloseContractMutation.mutate(
      {
        contractId: contractId,
        updateContractPayloadDto: {
          contractStatus: values.contractStatus,
          bindingDate: values.bindingDate.toDate(),
          premiumPaymentWarranty: values.premiumPaymentWarranty.toString(),
          brokerReference: values.brokerReference,
          installments: values.installments,
          closingReason: values.closingReason,
          competitorRateOnExposure: values.competitorRateOnExposure?.toString(),
          competitorRateOnLine: values.competitorRateOnLine?.toString(),
          ntuReason: values.ntuReason,
          isDeclaration: values.isDeclaration,
          rateMovement: values.rateMovement
            ? (Number(values.rateMovement) / 100).toString()
            : '0',
          originalRate: values.originalRate
            ? (Number(values.originalRate) / 100).toString()
            : '0',
          adjustedRate: values.adjustedRate
            ? (Number(values.adjustedRate) / 100).toString()
            : '0',
          brokerId: values.broker,
          reinsuredId: values.reinsured,
          basisOfCover: values.basisOfCover,
        },
        installmentDetails: [],
        declarationDetails: [],
      },
      {
        onSuccess: () => {
          message.success(t('contractDetail.closeSuccess'));
          handleBackToBindingDashboard();
        },
      }
    );
  }, [
    contractId,
    form,
    boundOrCloseContractMutation,
    t,
    handleBackToBindingDashboard,
  ]);

  const handleSaveSlipUpload = useCallback(async () => {
    if (!contractId) {
      return;
    }
    return uploadSlipContractMutation.mutate(
      {
        contractId: contractId,
        updateContractPayloadDto: {
          fileIds: fileList
            .filter((file) => !!file.id)
            .map((file) => file.id) as string[],
        },
      },
      {
        onSuccess: () => {
          message.success(t('contractDetail.saveSuccess'));
          handleBackToBindingDashboard();
        },
      }
    );
  }, [
    contractId,
    uploadSlipContractMutation,
    fileList,
    t,
    handleBackToBindingDashboard,
  ]);

  const handleSaveSlipUploadWithoutRate = useCallback(async () => {
    if (!contractId) {
      return;
    }

    const fileIds = fileList
      .filter((file) => !!file.id)
      .map((file) => file.id) as string[];

    const initialFileIds = contract?.files
      ? contract.files?.map((file) => file.fileId)
      : [];

    if (!isEqual(initialFileIds, fileIds)) {
      return uploadSlipContractMutation.mutate(
        {
          contractId: contractId,
          updateContractPayloadDto: {
            fileIds: fileList
              .filter((file) => !!file.id)
              .map((file) => file.id) as string[],
          },
        },
        {
          onSuccess: () => {
            message.success(t('contractDetail.saveSuccess'));
            handleBackToBindingDashboard();
          },
        }
      );
    }

    return handleBackToBindingDashboard();
  }, [
    contractId,
    uploadSlipContractMutation,
    fileList,
    t,
    handleBackToBindingDashboard,
  ]);

  const handleBindContract = useCallback(() => {
    if (!contractId) {
      return;
    }

    const values = form.getFieldsValue();
    const cedingCommissionRate = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.CEDING
    )?.rate;
    const sourceBrokerageRate = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.BROKERAGE
    )?.rate;
    const ereRate = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.ERE
    )?.rate;
    const gstOnEreRate = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.GST_ON_ERE
    )?.rate;
    const otherDeductionsAmount = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.OTHER_DEDUCTIONS
    )?.amount;
    const withholdingTaxRate = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.WITHHOLDING_TAX
    )?.rate;
    const taxAndFeeAmount = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.FEES
    )?.amount;
    const aitRate = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.AIT
    )?.rate;
    const gstRateOnCedingCommission = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.GST_CEDING
    )?.rate;
    const gstRateOnSourceBrokerage = binderScheduleData.find(
      (item) => item.key === EBinderScheduleKey.GST_BROKERAGE
    )?.rate;

    const fileIds = fileList
      .filter((file) => !!file.id)
      .map((file) => file.id) as string[];
    return boundOrCloseContractMutation.mutate(
      {
        contractId: contractId,
        updateContractPayloadDto: {
          transactionType: values.transactionType,
          contractStatus: values.contractStatus,
          policyType: values?.policyType,
          bindingDate: values.bindingDate.toDate(),
          premiumPaymentWarranty: values.premiumPaymentWarranty.toString(),
          brokerReference: values.brokerReference,
          installments: values.installments,
          incidental: values.incidental,
          layerPremium: layerPremium.toString(),
          incidentalCountry: values.incidentalCountry,
          underwritingComments: values.underwritingComments,
          notesAndEndorsements: values.notesAndEndorsements,
          rateMovement: values.rateMovement
            ? (Number(values.rateMovement) / 100).toString()
            : '0',
          originalRate: values.originalRate
            ? (Number(values.originalRate) / 100).toString()
            : '0',
          adjustedRate: values.adjustedRate
            ? (Number(values.adjustedRate) / 100).toString()
            : '0',
          cedingCommissionRate: (Number(cedingCommissionRate) / 100).toString(),
          sourceBrokerageRate: (Number(sourceBrokerageRate) / 100).toString(),
          ereRate: (Number(ereRate) / 100).toString(),
          gstOnEre: (Number(gstOnEreRate) / 100).toString(),
          otherDeductions: otherDeductionsAmount?.toString(),
          withholdingTaxRate: (Number(withholdingTaxRate) / 100).toString(),
          taxAndFeeAmount: taxAndFeeAmount?.toString(),
          binderShareSigned: (Number(values.binderShareSigned) / 100).toFixed(
            6
          ),
          aitRate: (Number(aitRate) / 100).toString(),
          gstRateOnCedingCommission: (
            Number(gstRateOnCedingCommission) / 100
          ).toString(),
          gstRateOnSourceBrokerage: (
            Number(gstRateOnSourceBrokerage) / 100
          ).toString(),
          fileIds,
          totalTiv: values.totalInsurableValue,
          underwriter: values.namedUnderwriterId,
          isDeclaration: values.isDeclaration,
          stockDeclaration: values.stockDeclaration,
          basisOfCover: values.basisOfCover,
          brokerId: values.broker,
          reinsuredId: values.reinsured,
          oldPinReferenceNumber: values.oldPinReferenceNumber,
        },
        installmentDetails:
          installments === true
            ? installmentData.map((item) => ({
                installmentPercent: item.installmentPercent,
                dueDate: item.installmentDueDate.startOf('day').toDate(),
                installmentNumber: item.id,
                totalAmountDue: item.totalAmountDue || 0,
              }))
            : [],
        declarationDetails:
          isDeclaration === true
            ? declarationData.map((item) => ({
                declarationName: item.declarationName,
                entityName: item.entityName,
                country: item.country,
                programOrder: item.programOrder.toString(),
                programOrderTotalPremium:
                  item.programOrderTotalPremium.toString(),
                shareOfPremiumSigned: item.shareOfPremiumSigned.toString(),
                dueDate: item.dueDate?.startOf('day').toDate() || new Date(),
              }))
            : [],
      },
      {
        onSuccess: () => {
          message.success(t('contractDetail.bindSuccess'));
          if (!haveFilesUploaded) {
            handleBackToBindingDashboard();
          } else {
            handleBackToBindingDashboard();
          }
        },
      }
    );
  }, [
    contractId,
    form,
    binderScheduleData,
    fileList,
    boundOrCloseContractMutation,
    installmentData,
    declarationData,
    t,
    haveFilesUploaded,
    handleBackToBindingDashboard,
  ]);

  const renderActionButtons = useMemo(() => {
    if (enableFileUpload) {
      return (
        <FormActionButtons
          includeCancel={false}
          onConfirm={handleSaveSlipUploadWithoutRate}
          confirmText={t('general.save')}
          confirmOutlined={false}
        />
      );
    }
    if (hideAllActions) {
      return <></>;
    }
    if (isSlipUpload) {
      return (
        <FormActionButtons
          confirmText={t('general.save')}
          cancelText={t('general.back')}
          isConfirmDisabled={!haveFilesUploaded}
          confirmOutlined={false}
          onCancel={handleBackToBindingDashboard}
          onConfirm={handleSaveSlipUpload}
          confirmTooltipTitle={
            !haveFilesUploaded
              ? t('contractDetail.pleaseUploadSlip')
              : undefined
          }
        />
      );
    }

    if (isCloseContractDetail) {
      return (
        <FormActionButtons
          cancelText={t('general.back')}
          includeConfirm={false}
          onCancel={handleBackToBindingDashboard}
          isConfirmDisabled={true}
        />
      );
    }

    return (
      <div className="flex gap-4 justify-between mt-6">
        <Button
          size="large"
          className={cn(
            'flex-1 text-blue-600 border-blue-600 hover:border-blue-500 hover:text-blue-500',
            isCancelledOrNotTakenUpContract && 'text-red-600'
          )}
          onClick={handleBackToBindingDashboard}
          danger={isCancelledOrNotTakenUpContract}
          color={isCancelledOrNotTakenUpContract ? 'danger' : 'default'}
        >
          {t('contractDetail.back')}
        </Button>
        {!isCancelledOrNotTakenUpContract && (
          <>
            {' '}
            <Button
              size="large"
              className="flex-1"
              disabled={showDetailContent || !isDataValid}
              color="danger"
              variant="outlined"
              onClick={handleCloseContract}
            >
              {t('contractDetail.close')}
            </Button>
            <Button
              type="primary"
              size="large"
              className="flex-1"
              disabled={showClosingReason || !isDataValid}
              onClick={handleBindContract}
            >
              {t('contractDetail.bind')}
            </Button>
          </>
        )}
      </div>
    );
  }, [
    hideAllActions,
    isSlipUpload,
    haveFilesUploaded,
    t,
    handleBackToBindingDashboard,
    handleSaveSlipUpload,
    handleCloseContract,
    handleBindContract,
    showDetailContent,
    isDataValid,
    showClosingReason,
    isCloseContractDetail,
    enableFileUpload,
  ]);

  return (
    <Form
      form={form}
      layout="vertical"
      onValuesChange={async (_, values) => {
        const isValid = await validateForm(
          values,
          form,
          Object.keys(values) as (keyof ContractDetailFormValues)[],
          getValidationSchema(t, { tbaReinsuredIds, tbaBrokerIds })
        );
        setIsFormValid(isValid);
      }}
      preserve={true}
    >
      <TransactionDetailsSection
        form={form}
        disabled={isFormDisabled}
        contract={contract}
        hiddenReferenceNumber={hiddenReferenceNumber}
        submissionTransactionType={submission.transactionType}
      />

      {/* Always render all sections but conditionally hide them with CSS */}
      <div className={`${showDetailContent ? 'block' : 'hidden'}`}>
        <IncidentalSection
          submissionId={submission.submissionId}
          form={form}
          disabled={isFormDisabled}
        />

        <BinderPlacementSection
          originalCurrency={submission.originalCurrency}
          form={form}
          contractCurrencyRate={
            contract?.rateToDefaultCurrency
              ? Number(contract.rateToDefaultCurrency)
              : 1
          }
          disabled={isFormDisabled}
        />

        <div className={`${installments ? 'block' : 'hidden'}`}>
          <InstallmentDetailsSection
            form={form}
            installmentData={installmentData}
            setInstallmentData={setInstallmentData}
            setIsInstallmentsValid={setIsInstallmentsValid}
            disabled={isFormDisabled}
          />
        </div>

        {isDeclaration && (
          <DeclarationDetailsSection
            form={form}
            declarationData={declarationData}
            setDeclarationData={setDeclarationData}
            setIsDeclarationValid={setIsDeclarationValid}
            disabled={isFormDisabled}
            layerPremium={layerPremium}
            contractStatus={originalContractStatus}
          />
        )}
        <NotesAndEndorsementsSection
          disabled={isFormDisabled}
          initialValues={{
            notesAndEndorsements: form.getFieldValue('notesAndEndorsements'),
          }}
          onValuesChange={(changedValues: any, allValues: any) => {
            form.setFieldsValue(allValues);
          }}
        />

        <FormSection title={t('contractDetail.binderSchedule')} collapsible>
          <SubmissionCurrencyTab
            originalCurrency={submission.originalCurrency}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            className="custom-tabs-with-bottom-curve-white"
            tabPosition="right"
          />
          <BinderScheduleSection
            originalCurrency={submission.originalCurrency}
            layerPremium={layerPremium}
            currency={activeTab}
            setBinderScheduleData={setBinderScheduleData}
            binderScheduleData={binderScheduleData}
            binderShareSigned={binderShareSigned ?? 0}
            brokerCountry={contract?.broker?.country}
            layerTotalDeductions={contract?.layer?.totalDeductions}
            binderPinCommission={contract?.binder?.pinCommission}
            contractCurrencyRate={
              contract?.rateToDefaultCurrency
                ? Number(contract.rateToDefaultCurrency)
                : 1
            }
            disabled={isFormDisabled}
            setIsBinderScheduleValid={setIsBinderScheduleValid}
            allowPremiumEdit={false}
            contractStatus={contractStatus}
          />
        </FormSection>

        {/* File Upload Section */}
        <Form.Item className="mt-6" preserve={true}>
          {fileSessionId && (
            <FileUploader
              sessionId={fileSessionId}
              entityType={CreateUploadedFilePayloadDtoEntityTypeEnum.Contract}
              fileList={fileList}
              setFileList={setFileList}
              disabled={disabled && !enableFileUpload}
            />
          )}
        </Form.Item>
      </div>

      {/* Provide Reason for Closing Case Section */}
      <div className={`${showClosingReason ? 'block' : 'hidden'}`}>
        <ClosingReasonSection disabled={isFormDisabled} form={form} />
      </div>

      {renderActionButtons}
    </Form>
  );
};

export default ContractDetail;
